// ** react imports
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// ** react router imports
import { useParams, useSearchParams } from 'react-router-dom';

// ** custom hook imports
import {
  useGetTaxFile,
  useGetQuizResponseByTaxFileId,
} from '../hooks/apis/useTaxFile';
import { useGetSectionsWithQuestions } from '../hooks/apis/useQuizTemplate';

// ** enums imports
import { TAX_FILE_TYPE } from '../enums/common';

const TaxFileDetailsContext = createContext(null);

export function TaxFileDetailsProvider({ children }) {
  // hooks
  const { taxFileId } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');

  // states
  const [quizResponse, setQuizResponse] = useState({});
  const [taxFile, setTaxFile] = useState({});
  const [quizSessionId, setQuizSessionId] = useState('');

  // api hooks
  const {
    data: quizResponseData,
    isGetQuizResponseByTaxFileIdLoading,
    isGetQuizResponseByTaxFileIdError,
    isGetQuizResponseByTaxFileIdFetching,
    getQuizResponseByTaxFileId,
    refetchGetQuizResponseByTaxFileId,
  } = useGetQuizResponseByTaxFileId();

  const {
    data: taxFileData,
    isGetTaxFileLoading,
    isGetTaxFileError,
    getTaxFile,
    refetchGetTaxFile,
  } = useGetTaxFile();

  const {
    data: allSectionWithQuestion,
    isGetSectionsWithQuestionsLoading,
    isGetSectionsWithQuestionsError,
    getSectionsWithQuestions,
    refetchGetSectionsWithQuestions,
  } = useGetSectionsWithQuestions();

  // hooks
  useEffect(() => {
    if (type === TAX_FILE_TYPE.VIRTUAL_INTAKE) {
      getQuizResponseByTaxFileId(taxFileId);
    } else if (type === TAX_FILE_TYPE.IN_OFFICE) {
      getTaxFile(taxFileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxFileId, type]);

  useEffect(() => {
    if (
      type === TAX_FILE_TYPE.VIRTUAL_INTAKE &&
      quizResponseData !== undefined
    ) {
      const templateId = quizResponseData.quizResponse?.templateId._id;
      if (templateId) {
        getSectionsWithQuestions(templateId);
      }

      setQuizResponse(quizResponseData);

      const taxFileFromQuiz = quizResponseData.taxFile;
      if (taxFileFromQuiz) {
        setTaxFile(taxFileFromQuiz);
      }
      const quizSessionId = quizResponseData.sessionId;
      if (quizSessionId) {
        setQuizSessionId(quizSessionId);
      }
    } else if (type === TAX_FILE_TYPE.IN_OFFICE && taxFileData !== undefined) {
      setTaxFile(taxFileData.taxFile || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizResponseData, taxFileData]);

  const value = useMemo(
    () => ({
      taxFile,
      quizResponse,
      quizSessionId,
      allSectionWithQuestion,
      isGetQuizResponseByTaxFileIdLoading,
      isGetQuizResponseByTaxFileIdFetching,
      isGetTaxFileLoading,
      isGetSectionsWithQuestionsLoading,
      isGetQuizResponseByTaxFileIdError,
      isGetTaxFileError,
      isGetSectionsWithQuestionsError,
      refetchGetQuizResponseByTaxFileId,
      refetchGetTaxFile,
      refetchGetSectionsWithQuestions,
    }),
    [
      taxFile,
      quizResponse,
      quizSessionId,
      allSectionWithQuestion,
      isGetQuizResponseByTaxFileIdLoading,
      isGetQuizResponseByTaxFileIdFetching,
      isGetTaxFileLoading,
      isGetSectionsWithQuestionsLoading,
      isGetQuizResponseByTaxFileIdError,
      isGetTaxFileError,
      isGetSectionsWithQuestionsError,
      refetchGetQuizResponseByTaxFileId,
      refetchGetTaxFile,
      refetchGetSectionsWithQuestions,
    ]
  );

  return (
    <TaxFileDetailsContext.Provider value={value}>
      {children}
    </TaxFileDetailsContext.Provider>
  );
}

export function useTaxFileDetails() {
  const context = useContext(TaxFileDetailsContext);
  if (!context) {
    throw new Error(
      'useTaxFileDetails must be used within TaxFileDetailsProvider'
    );
  }

  return context;
}
